import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** The caller's preferences, as the backend returns them: key → value. */
export type UserPreferenceMap = Record<string, unknown>

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/**
 * The signed-in user's own UI preferences.
 *
 * There is no user id in any of these calls, and that is deliberate: the backend
 * resolves the owner from the bearer token, so there is no way to address
 * someone else's preferences. See `UserPreferencesController`.
 */
export const userPreferencesApi = {
  /**
   * Every preference in one request.
   *
   * The whole map rather than one key at a time: the app hydrates ~46 table
   * layouts on load, and one request per table would be its own performance bug.
   */
  getAll: () => request<UserPreferenceMap>('/preferences'),

  /**
   * Create or replace one preference.
   *
   * `keepalive` is for the save that fires while the page is going away
   * (`pagehide`). A normal fetch is cancelled when the document unloads, which
   * would silently lose a change still inside its debounce window; `keepalive`
   * lets the request outlive the page. It caps the body at 64KB, comfortably
   * above the server's 32KB per-preference limit.
   */
  set: (key: string, value: unknown, opts?: { keepalive?: boolean }) =>
    request<{ value: unknown }>(`/preferences/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
      keepalive: opts?.keepalive,
    }),

  remove: (key: string, opts?: { keepalive?: boolean }) =>
    request<{ ok: true }>(`/preferences/${encodeURIComponent(key)}`, {
      method: 'DELETE',
      keepalive: opts?.keepalive,
    }),

  /** Remove every preference, or only those under `prefix`. */
  removeAll: (prefix?: string) =>
    request<{ count: number }>(
      `/preferences${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`,
      { method: 'DELETE' },
    ),
}
